import { useEffect, useRef, useState } from "react";
import { ArrowRight, Plus, X } from "lucide-react";
import { useInbox, useMarkThreadRead, useSendMessage, type Blogger } from "@/shared/api/wb";
import { Avatar, PlatformIcon, type Platform } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

export function WriteToModal({ open, onClose, campaignId, blogger }: {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  blogger: Blogger;
}) {
  const { data: inbox } = useInbox();
  const [text, setText] = useState("");
  const send = useSendMessage(campaignId);
  const markRead = useMarkThreadRead();
  const scrollRef = useRef<HTMLDivElement>(null);

  const thread = inbox?.threads.find(
    (t) => t.blogger_id === blogger.id && t.campaign_id === campaignId,
  ) ?? null;

  const quickReplies = inbox?.quick_replies ?? [];

  useEffect(() => {
    if (thread && thread.unread > 0) {
      markRead.mutate({ campaignId, bloggerId: blogger.id });
    }
  }, [thread?.unread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?.messages?.length]);

  function onSend(content?: string) {
    const t = (content ?? text).trim();
    if (!t) return;
    send.mutate({ blogger_id: blogger.id, text: t });
    setText("");
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] grid place-items-center p-4"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] rounded-xl shadow-xl overflow-hidden flex flex-col fade-up"
        style={{ width: "min(500px, 96vw)", height: "min(520px, 90vh)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-ink-12 flex-shrink-0 bg-[var(--bg-2)]">
          <Avatar name={blogger.full} size={32} grad={blogger.grad as [string, string]} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 font-display font-semibold text-[14px]">
              <PlatformIcon platform={blogger.platform as Platform} size={12} />
              {blogger.name}
            </div>
            <div className="text-[11px] text-ink-40 truncate">{blogger.full}</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6 text-ink-40 hover:text-ink"
          >
            <X size={16} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-1">
          {thread ? (
            thread.messages.map((m, i) => (
              <div
                key={i}
                className={cn("flex flex-col gap-0.5 max-w-[75%]",
                  m.from_who === "me" ? "self-end items-end" : "self-start items-start")}
              >
                <div className={cn("px-3.5 py-2 rounded-md text-[13.5px] leading-relaxed",
                  m.from_who === "me"
                    ? "bg-lime text-[var(--fg-on-accent)]"
                    : "bg-white border border-ink-12 text-ink")}>
                  {m.text}
                </div>
                <div className="text-[10.5px] text-ink-40">{m.time_label}</div>
              </div>
            ))
          ) : (
            <div className="m-auto text-center">
              <div className="text-[13px] text-ink-40 mb-1">Диалог ещё не начат</div>
              <div className="text-[11.5px] text-ink-30">Отправьте первое сообщение блогеру</div>
            </div>
          )}
        </div>

        <div className="border-t border-ink-12 px-4 py-3 bg-white flex-shrink-0">
          {quickReplies.length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {quickReplies.map((qr) => (
                <button
                  key={qr}
                  onClick={() => onSend(qr)}
                  className="h-7 px-2.5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40"
                >
                  {qr}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-center">
            <button
              title="Прикрепить файл"
              className="w-9 h-9 grid place-items-center rounded-md text-ink-60 hover:bg-ink-6 hover:text-ink"
            >
              <Plus size={18} />
            </button>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && onSend()}
              placeholder="Сообщение…"
              className="flex-1 h-10 px-4 rounded-md border border-ink-12 bg-white text-[14px] outline-none focus:border-ink"
            />
            <button
              onClick={() => onSend()}
              disabled={!text.trim() || send.isPending}
              className="h-10 w-10 grid place-items-center rounded-md bg-lime text-[var(--fg-on-accent)] disabled:opacity-40 hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
            >
              <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
